import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchTotems,
  fetchTotemStats,
  createTotem,
  updateTotem,
  updateTotemStatus,
  moveTotemsBetweenStatuses,
  assignTotemsToClient,
  deleteTotem,
  generateTotemCode,
  getReturnableTotemFleetSummary,
  TotemFilters,
  TotemWithRelations,
  TotemStats,
  TotemFleetStatusCount,
} from "~/services/totemService";
import { Database } from "~/types/supabase";
import { toast } from "sonner";
import {
  TOTEM_DETAIL_QUERY_KEY,
  TOTEM_MOVEMENTS_QUERY_KEY,
} from "./useTotemDetail";

type TotemStatus = Database["public"]["Enums"]["totem_status"];
type TotemInsert = Database["public"]["Tables"]["totems"]["Insert"];
type TotemUpdate = Database["public"]["Tables"]["totems"]["Update"];

export const TOTEMS_QUERY_KEY = ["totems"];
export const TOTEM_STATS_QUERY_KEY = ["totem-stats"];

export function useTotems(filters?: TotemFilters) {
  return useQuery<TotemWithRelations[], Error>({
    queryKey: [...TOTEMS_QUERY_KEY, filters],
    queryFn: () => fetchTotems(filters),
  });
}

export function useTotemStats() {
  return useQuery<TotemStats, Error>({
    queryKey: TOTEM_STATS_QUERY_KEY,
    queryFn: fetchTotemStats,
  });
}

export function useReturnableTotemFleetSummary(enabled = true) {
  return useQuery<TotemFleetStatusCount[], Error>({
    queryKey: ["returnable-totem-fleet-summary"],
    queryFn: getReturnableTotemFleetSummary,
    enabled,
  });
}

export function useGenerateTotemCode() {
  return useQuery<string, Error>({
    queryKey: ["totem-code"],
    queryFn: generateTotemCode,
    staleTime: 0,
    gcTime: 0,
  });
}

export function useCreateTotem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      totem: Omit<TotemInsert, "id" | "created_at" | "updated_at">
    ) => createTotem(totem),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TOTEMS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: TOTEM_STATS_QUERY_KEY });
      toast.success("Totem creado exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al crear totem: ${error.message}`);
    },
  });
}

export function useUpdateTotem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: TotemUpdate }) =>
      updateTotem(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TOTEMS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: TOTEM_STATS_QUERY_KEY });
      toast.success("Totem actualizado");
    },
    onError: (error: Error) => {
      toast.error(`Error al actualizar: ${error.message}`);
    },
  });
}

export function useUpdateTotemStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      status,
      performedBy,
      options,
    }: {
      id: string;
      status: TotemStatus;
      performedBy: string;
      options?: {
        notes?: string;
        clientId?: string;
        salesRequestId?: string;
        pickupOrderId?: string;
      };
    }) => updateTotemStatus(id, status, performedBy, options),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TOTEMS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: TOTEM_STATS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: TOTEM_DETAIL_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: TOTEM_MOVEMENTS_QUERY_KEY });
      toast.success("Estado actualizado");
    },
    onError: (error: Error) => {
      toast.error(`Error al actualizar estado: ${error.message}`);
    },
  });
}

export function useMoveTotems() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: {
      fromStatuses: TotemStatus[];
      toStatus: TotemStatus;
      quantity: number;
      performedBy: string;
      notes?: string;
      clientId?: string;
    }) => moveTotemsBetweenStatuses(params),
    onSuccess: (moved) => {
      queryClient.invalidateQueries({ queryKey: TOTEMS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: TOTEM_STATS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: TOTEM_DETAIL_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: TOTEM_MOVEMENTS_QUERY_KEY });
      toast.success(`${moved} tótem(s) movidos`);
    },
    onError: (error: Error) => {
      toast.error(`Error al mover totems: ${error.message}`);
    },
  });
}

export function useAssignTotemsToClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: {
      fromStatuses: TotemStatus[];
      quantity: number;
      clientId: string;
      performedBy: string;
      notes?: string;
    }) => assignTotemsToClient(params),
    onSuccess: (moved) => {
      queryClient.invalidateQueries({ queryKey: TOTEMS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: TOTEM_STATS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: TOTEM_DETAIL_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: TOTEM_MOVEMENTS_QUERY_KEY });
      toast.success(`${moved} tótem(s) asignados al cliente`);
    },
    onError: (error: Error) => {
      toast.error(`Error al asignar tótems: ${error.message}`);
    },
  });
}

export function useDeleteTotem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteTotem(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TOTEMS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: TOTEM_STATS_QUERY_KEY });
      toast.success("Totem eliminado");
    },
    onError: (error: Error) => {
      toast.error(`Error al eliminar: ${error.message}`);
    },
  });
}
